import { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import api, { resolveMedia } from "@/lib/api";
import { PORTFOLIO } from "@/constants/testIds";
import useSEO from "@/lib/useSEO";
import { Reveal, MaskedLine, EASE } from "@/components/motion/Reveal";

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Tutti");
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portfolio").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(items.map((i) => i.category)));
    return ["Tutti", ...cats];
  }, [items]);

  const filtered = filter === "Tutti" ? items : items.filter((i) => i.category === filter);
  const gallery = selected ? [selected.cover_image, ...(selected.images || [])].filter(Boolean).map(resolveMedia) : [];
  useSEO({ title: "Portfolio", description: "Progetti di interior design residenziali, ristrutturazioni e home staging realizzati a Napoli e in Campania." });

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox.open) {
        if (e.key === "Escape") setLightbox({ open: false, index: 0 });
        if (e.key === "ArrowRight") setLightbox((l) => ({ ...l, index: (l.index + 1) % gallery.length }));
        if (e.key === "ArrowLeft") setLightbox((l) => ({ ...l, index: (l.index - 1 + gallery.length) % gallery.length }));
      } else if (e.key === "Escape" && selected) {
        setSelected(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open, gallery.length, selected]);

  // Blocca lo scroll della pagina quando modal/lightbox sono aperti; ripristina alla chiusura
  useEffect(() => {
    if (!selected) return;
    const prevOverflow = document.body.style.overflow;
    const prevPad = document.body.style.paddingRight;
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPad;
    };
  }, [selected]);

  return (
    <div data-testid="page-portfolio">
      <section className="container-x pt-10 pb-16">
        <motion.span className="eyebrow block" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>Portfolio</motion.span>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-6 max-w-4xl">
          <MaskedLine delay={0.15}>Progetti che</MaskedLine>
          <MaskedLine delay={0.32}><em className="text-gold font-light">respirano.</em></MaskedLine>
        </h1>
        <motion.p className="mt-8 text-taupe max-w-2xl text-lg font-light" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5, ease: EASE }}>
          Una selezione di lavori residenziali, ristrutturazioni e home staging.
        </motion.p>
      </section>

      <section className="container-x mb-10">
        <div className="flex flex-wrap gap-2 border-y border-hair py-4">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={PORTFOLIO.filter(c.toLowerCase().replace(/\s+/g, "-"))}
              className={`px-4 py-2 text-xs tracking-[0.14em] uppercase transition-colors ${
                filter === c ? "bg-ink text-bone" : "text-ink/70 hover:text-ink"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="container-x pb-24">
        {loading ? (
          <div className="text-taupe text-center py-24">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="text-taupe text-center py-24">Nessun progetto disponibile.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={PORTFOLIO.grid}>
            {filtered.map((p, idx) => (
              <Reveal key={p.id} delay={(idx % 3) * 0.1} className={idx % 3 === 1 ? "lg:mt-16" : ""}>
              <button
                onClick={() => setSelected(p)}
                data-testid={PORTFOLIO.item(p.id)}
                className="group text-left w-full"
              >
                <div className="overflow-hidden">
                  <img src={resolveMedia(p.cover_image)} alt={p.title} className="w-full h-[440px] object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="mt-5 flex justify-between items-baseline">
                  <div>
                    <div className="text-xs tracking-[0.14em] uppercase text-gold">{p.category}</div>
                    <div className="font-serif text-2xl mt-1">{p.title}</div>
                    <div className="text-xs text-taupe mt-1">{p.location} {p.year && `· ${p.year}`}</div>
                  </div>
                  <span className="text-xs tracking-[0.14em] uppercase text-taupe">
                    {(p.images?.length || 0) + 1} foto
                  </span>
                </div>
              </button>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* Project detail modal with gallery */}
      {selected && !lightbox.open && (
        <div className="fixed inset-0 z-[60] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-lenis-prevent onClick={() => setSelected(null)}>
          <div className="relative max-w-6xl w-full bg-bone" onClick={(e) => e.stopPropagation()} data-testid="portfolio-modal">
            <button onClick={() => setSelected(null)} className="fixed top-4 right-4 z-[65] bg-bone p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gold hover:text-bone transition-colors shadow-md" data-testid="portfolio-close-modal" aria-label="Chiudi">
              <X size={18} />
            </button>
            <button
              onClick={() => setLightbox({ open: true, index: 0 })}
              className="block w-full text-left"
              data-testid="portfolio-open-lightbox"
              aria-label="Apri galleria"
            >
              <img src={resolveMedia(selected.cover_image)} alt={selected.title} className="w-full h-[420px] md:h-[520px] object-cover" />
            </button>
            <div className="p-8 md:p-12">
              <div className="text-xs tracking-[0.14em] uppercase text-gold">{selected.category}</div>
              <h3 className="font-serif text-3xl md:text-5xl mt-3">{selected.title}</h3>
              <div className="text-sm text-taupe mt-2">{selected.location} {selected.year && `· ${selected.year}`}</div>
              {selected.description && (
                <p className="mt-6 text-taupe leading-[1.9] font-light max-w-3xl">{selected.description}</p>
              )}
              {gallery.length > 1 && (
                <>
                  <div className="mt-10 eyebrow">Galleria · {gallery.length} foto</div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {gallery.map((src, i) => (
                      <button
                        key={i}
                        onClick={() => setLightbox({ open: true, index: i })}
                        className="group relative overflow-hidden"
                        data-testid={`portfolio-thumb-${i}`}
                        aria-label={`Apri foto ${i + 1}`}
                      >
                        <img src={src} alt={`${selected.title} — ${i + 1}`} className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105" />
                        <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/20 transition-colors" />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {selected && lightbox.open && gallery.length > 0 && (
        <div className="fixed inset-0 z-[70] bg-ink/95 flex flex-col overflow-hidden" data-testid="portfolio-lightbox" data-lenis-prevent>
          <div className="flex justify-between items-center p-4 md:p-6 text-bone shrink-0">
            <div className="text-sm min-w-0">
              <span className="eyebrow text-gold">{selected.category}</span>
              <div className="font-serif text-lg md:text-2xl mt-1 truncate">{selected.title}</div>
            </div>
            <div className="flex items-center gap-4 md:gap-6 shrink-0">
              <span className="text-sm text-bone/70 whitespace-nowrap" data-testid="lightbox-counter">{lightbox.index + 1} di {gallery.length}</span>
              <button onClick={() => setLightbox({ open: false, index: 0 })} className="hover:text-gold transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="lightbox-close" aria-label="Chiudi">
                <X size={22} />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <div className="min-h-full flex items-center justify-center p-4 md:p-8">
              <img src={gallery[lightbox.index]} alt={`${selected.title} — foto ${lightbox.index + 1}`} className="max-w-full w-auto h-auto object-contain select-none" />
            </div>
          </div>
          {gallery.length > 1 && (
            <>
              <button
                onClick={() => setLightbox((l) => ({ ...l, index: (l.index - 1 + gallery.length) % gallery.length }))}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-bone/10 hover:bg-gold text-bone hover:text-ink p-3 backdrop-blur-sm transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                data-testid="lightbox-prev"
                aria-label="Precedente"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={() => setLightbox((l) => ({ ...l, index: (l.index + 1) % gallery.length }))}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-bone/10 hover:bg-gold text-bone hover:text-ink p-3 backdrop-blur-sm transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                data-testid="lightbox-next"
                aria-label="Successiva"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
          {gallery.length > 1 && (
            <div className="border-t border-bone/10 p-4 overflow-x-auto shrink-0" data-lenis-prevent>
              <div className="flex gap-2 justify-center min-w-min">
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setLightbox({ open: true, index: i })}
                    className={`shrink-0 border-2 transition-all ${i === lightbox.index ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"}`}
                    data-testid={`lightbox-thumb-${i}`}
                  >
                    <img src={src} alt="" className="w-16 h-16 md:w-20 md:h-20 object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
